import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { billing, flows } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useBots, botName } from '../../../../lib/bots';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * Add a chatbot (21 September 2026): a name, and optionally another chatbot
 * to start from, so a second brand can begin with the first one's tone, word
 * lists and notices instead of a blank page. Its flows and the accounts it
 * answers on are set up afterwards, on its own page.
 *
 * Added from Settings, it opens the new chatbot's page. Added from somewhere
 * else (the Flows sidebar, 22 September 2026), `onAdded` is called instead and
 * the person stays where they were, with the new chatbot chosen.
 */
export function AddBotModal({ opened, onClose, onAdded }: {
  opened: boolean;
  onClose: () => void;
  onAdded?: (botId: string) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { bots, setActive } = useBots();
  const fallback = t('settings.assistant.defaultName');
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState(bots.find((b) => b.is_default)?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => flows.createBot({ name: name.trim(), copy_from_bot_id: copyFrom || undefined }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['bots'] });
      setActive(res.data.id);
      notifySuccess(t('bots.add.done', { name: botName(res.data, fallback) }), t('settings.assistant.title'));
      onClose();
      if (onAdded) onAdded(res.data.id);
      else nav(`/settings/assistant/${res.data.id}`);
    },
    // The plan's allowance is refused in a sentence written for the customer
    // ("You are on Lite, which includes 1 bot. Upgrade to add more."); show it as it is.
    onError: (e) => setError(billing.isPlanLimitReached(e) || e instanceof Error ? (e as Error).message : t('common.unknownError'))
  });

  const nameOk = name.trim().length > 0 && name.trim().length <= 40;

  return (
    <Modal opened={opened} onClose={onClose} title={t('bots.add.title')} size="md">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('bots.add.intro')}</Text>
        <TextInput
          label={t('settings.assistant.name')}
          description={t('settings.assistant.nameHint')}
          value={name}
          maxLength={40}
          placeholder={'Thabo' /* i18n-ignore: an example name */}
          onChange={(e) => { setName(e.currentTarget.value); setError(null); }}
        />
        <Select
          label={t('bots.add.copyFrom')}
          description={t('bots.add.copyFromHint')}
          value={copyFrom}
          onChange={(e) => setCopyFrom(e.currentTarget.value)}
          data={[
            { value: '', label: t('bots.add.copyNothing') },
            ...bots.map((b) => ({ value: b.id, label: botName(b, fallback) }))
          ]}
        />
        {error && <Alert color="error">{error}</Alert>}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button disabled={!nameOk} loading={create.isPending} onClick={() => create.mutate()}>{t('bots.add.action')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
